import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, CheckCircle, Download } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const RESP_COLORS = {
  inovit: "bg-orange-100 text-orange-700",
  gmr: "bg-red-100 text-red-700",
  discom: "bg-blue-100 text-blue-700",
  site_condition: "bg-yellow-100 text-yellow-700",
  external: "bg-slate-100 text-slate-600"
};

const STATUS_LABELS = {
  pending: "PENDING", ci_done: "CI DONE", dtm_done: "DTM DONE",
  dtc_done: "DTC DONE", mi_done: "MI DONE", qc_passed: "QC PASSED", rework: "REWORK"
};

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-600",
  ci_done: "bg-blue-100 text-blue-700",
  dtm_done: "bg-purple-100 text-purple-700",
  dtc_done: "bg-yellow-100 text-yellow-700",
  mi_done: "bg-orange-100 text-orange-700",
  qc_passed: "bg-green-100 text-green-700",
  rework: "bg-red-100 text-red-700",
};

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return ""; }
};

export default function HoldsPage() {
  const { user } = useAuth();
  const [holds, setHolds] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [reasons, setReasons] = useState([]);
  const [respFilter, setRespFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [dts, setDTs] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [selectedFeeder, setSelectedFeeder] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    dt_id: "", hold_reason_code: "", responsibility: "inovit",
    hold_notes: "", is_excluded: false
  });

  const fetchHolds = async () => {
    setLoading(true);
    try {
      const params = { is_active: true, limit: 200 };
      if (respFilter) params.responsibility = respFilter;
      const [holdsRes, statsRes] = await Promise.all([
        api.get("/holds", { params }),
        api.get("/holds/stats")
      ]);
      setHolds(holdsRes.data.items);
      setTotal(holdsRes.data.total);
      setStats(statsRes.data);
    } catch { toast.error("Failed to load holds"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchHolds();
    api.get("/hold-reasons").then(r => setReasons(r.data)).catch(() => {});
    api.get("/dt-master", { params: { limit: 500, hold_status: "none" } }).then(r => setDTs(r.data.items || [])).catch(() => {});
    api.get("/hierarchy/feeders").then(r => setFeeders(r.data || [])).catch(() => {});
  }, [respFilter]);

  const handleResolve = async (id) => {
    const notes = prompt("Resolution notes:");
    if (notes === null) return;
    try {
      await api.put(`/holds/${id}/resolve`, { resolution_notes: notes });
      toast.success("Hold resolved");
      fetchHolds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resolve hold"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.dt_id) { toast.error("Please select a DT"); return; }
    if (!form.hold_reason_code) { toast.error("Hold reason is required"); return; }
    setCreating(true);
    try {
      await api.post("/holds", form);
      toast.success("Hold placed successfully");
      setShowCreate(false);
      setSelectedFeeder("");
      setForm({ dt_id: "", hold_reason_code: "", responsibility: "inovit", hold_notes: "", is_excluded: false });
      fetchHolds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const handleExport = () => {
    if (holds.length === 0) { toast.error("No holds to export"); return; }
    const rows = holds.map(h => ({
      "DT Code": h.dt_code || "",
      "DT Name": h.dt_name || "",
      "Feeder": h.feeder_name || h.feeder_code || "",
      "Location": h.location_name || "",
      "Capacity (kVA)": h.transformer_capacity_kva || "",
      "Status": STATUS_LABELS[h.lifecycle_status] || h.lifecycle_status || "",
      "Hold Reason": h.reason_details?.reason_name || h.hold_reason_code || "",
      "Responsibility": (h.responsibility || "").toUpperCase(),
      "Remarks": h.hold_notes || "",
      "Reported By": h.reported_by_name || h.reported_by || "",
      "Date": fmtDate(h.reported_at),
      "Circle": h.circle_name || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holds");
    XLSX.writeFile(wb, `Hold_Tracking_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Exported ${rows.length} holds`);
  };

  const canAction = ["admin", "ci", "supervisor"].includes(user?.role);

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="holds-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Hold Tracking</h1>
          <p className="text-slate-500 text-sm">{total} active holds</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-sm text-xs font-bold hover:bg-green-700" data-testid="export-holds-btn">
            <Download size={14} /> Export
          </button>
          {canAction && (
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-sm text-sm font-bold hover:bg-red-600" data-testid="add-hold-btn">
              <Plus size={16} />Place Hold
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
          <div className="text-2xl font-black text-red-500">{stats.total || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Total Active</div>
        </div>
        {Object.entries(stats.by_responsibility || {}).map(([k, v]) => (
          <div key={k} className={`rounded-sm p-3 text-center ${RESP_COLORS[k] || "bg-slate-100"}`}>
            <div className="text-2xl font-black">{v}</div>
            <div className="text-[10px] uppercase tracking-wide">{k}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex gap-3">
        <select className="field-select h-9 text-xs min-w-[180px]" value={respFilter} onChange={e => setRespFilter(e.target.value)} data-testid="resp-filter">
          <option value="">All Responsibilities</option>
          {["inovit", "gmr", "discom", "site_condition", "external"].map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="holds-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">DT Code</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">DT Name</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Feeder</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Location</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">kVA</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Hold Reason</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Responsibility</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Remarks</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Reported By</th>
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                {canAction && <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i}>{[...Array(canAction ? 12 : 11)].map((_, j) => <td key={j} className="px-2 py-2"><div className="h-3.5 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>)
              ) : holds.length === 0 ? (
                <tr><td colSpan={canAction ? 12 : 11} className="text-center py-12 text-slate-400">
                  <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />No active holds
                </td></tr>
              ) : holds.map(hold => (
                <tr key={hold.id} className="hover:bg-slate-50/80">
                  <td className="px-2 py-1.5"><span className="font-mono text-xs font-bold text-slate-800">{hold.dt_code || "—"}</span></td>
                  <td className="px-2 py-1.5 text-xs text-slate-600 max-w-[120px] truncate">{hold.dt_name || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">{hold.feeder_name || hold.feeder_code || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500 max-w-[100px] truncate">{hold.location_name || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{hold.transformer_capacity_kva || "—"}</td>
                  <td className="px-2 py-1.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${STATUS_COLORS[hold.lifecycle_status] || "bg-slate-100 text-slate-600"}`}>
                      {STATUS_LABELS[hold.lifecycle_status] || hold.lifecycle_status || "—"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="text-xs font-semibold text-slate-700">{hold.reason_details?.reason_name || hold.hold_reason_code}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${RESP_COLORS[hold.responsibility] || "bg-slate-100"}`}>{hold.responsibility}</span>
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-slate-400 max-w-[150px] truncate">{hold.hold_notes || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500">{hold.reported_by_name || hold.reported_by?.slice(-6) || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmtDate(hold.reported_at)}</td>
                  {canAction && (
                    <td className="px-2 py-1.5">
                      <button onClick={() => handleResolve(hold.id)} className="text-xs text-green-600 font-bold hover:text-green-700" data-testid={`resolve-hold-${hold.id}`}>
                        Resolve
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Hold Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] bg-black/60" onClick={() => setShowCreate(false)}>
          <div className="fixed top-12 left-1/2 -translate-x-1/2 w-full max-w-md bottom-4 bg-white rounded-sm shadow-xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 border-b border-slate-100 shrink-0">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Place Hold on DT</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="field-label">Select Feeder</label>
                  <select className="field-select" value={selectedFeeder} onChange={e => { setSelectedFeeder(e.target.value); setForm({ ...form, dt_id: "" }); }} data-testid="hold-feeder-select">
                    <option value="">All Feeders</option>
                    {feeders.map(f => <option key={f.feeder_code} value={f.feeder_code}>{f.feeder_code} - {f.feeder_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Select DT *</label>
                  <select className="field-select" required value={form.dt_id} onChange={e => setForm({ ...form, dt_id: e.target.value })} data-testid="hold-dt-select">
                    <option value="">Select DT...</option>
                    {dts.filter(d => !selectedFeeder || d.feeder_code === selectedFeeder).map(d => <option key={d.id} value={d.id}>{d.dt_code} — {d.dt_name || "Unnamed"}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Hold Reason *</label>
                  <select className="field-select" required value={form.hold_reason_code} onChange={e => {
                    const r = reasons.find(r => r.code === e.target.value);
                    setForm({ ...form, hold_reason_code: e.target.value, responsibility: r?.responsibility || "inovit" });
                  }} data-testid="hold-reason-create-select">
                    <option value="">Select reason...</option>
                    {reasons.map(r => <option key={r.code} value={r.code}>[{r.responsibility?.toUpperCase()}] {r.reason_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Responsibility</label>
                  <select className="field-select" value={form.responsibility} onChange={e => setForm({ ...form, responsibility: e.target.value })}>
                    {["inovit", "gmr", "discom", "site_condition", "external"].map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Notes / Remarks</label>
                  <textarea className="field-input h-20 resize-none" value={form.hold_notes} onChange={e => setForm({ ...form, hold_notes: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_excluded} onChange={e => setForm({ ...form, is_excluded: e.target.checked })} />
                  <span className="font-medium text-slate-700">Exclude from scope</span>
                </label>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
                  <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-sm text-sm font-bold hover:bg-red-600 disabled:opacity-60">
                    {creating ? "Placing..." : "Place Hold"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
